import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import type { Row } from "@tanstack/react-table"
import type { FC } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { COST_BREAKDOWN_CURRENCY_FIELD_PROPS } from "../../constants"
import type { CostBreakdownFormValues, CostBreakdownProductsFormValues } from "../../schemas"
import { costBreakdownProductsFormSchema } from "../../schemas"
import { getCostBreakdownProductsDefaultValues } from "../../utils/formDefaults"

import { CostBreakdownProductsExpandedContent } from "./CostBreakdownProductsExpandedContent"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FormNumericField } from "@/components/UiKit"
import type { GoodsReceipt } from "@/graphql/codegen/graphql"
import { CostBreakdownPurchaseOrdersTable } from "@/screens/Procurement/CostBreakdown"
import type { PurchaseOrderLineWithReceiptContext } from "@/screens/WarehouseReceiving/types"

interface CostBreakdownProductsDialogProps {
  goodsReceipt?: GoodsReceipt
  grCostBreakdown?: CostBreakdownFormValues
  productsCostBreakdown?: CostBreakdownProductsFormValues
  purchaseOrderLineId?: string
  isSubmitting?: boolean
  onCancel: (isDirty: boolean, currentData?: CostBreakdownProductsFormValues) => void
  onSubmit: (grCostBreakdown: CostBreakdownFormValues, productsCostBreakdown: CostBreakdownProductsFormValues) => void
  onBack: (currentProductsData?: CostBreakdownProductsFormValues) => void
}

export const CostBreakdownProductsDialog: FC<CostBreakdownProductsDialogProps> = ({
  goodsReceipt,
  grCostBreakdown,
  productsCostBreakdown,
  purchaseOrderLineId,
  isSubmitting = false,
  onCancel,
  onSubmit,
  onBack,
}) => {
  const isEditMode = !!productsCostBreakdown

  const methods = useForm<CostBreakdownProductsFormValues>({
    resolver: zodResolver(costBreakdownProductsFormSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: getCostBreakdownProductsDefaultValues(goodsReceipt, grCostBreakdown, productsCostBreakdown),
  })

  const {
    handleSubmit,
    getValues,
    formState: { isValid, isDirty },
  } = methods

  const handleFormSubmit = (productsCostBreakdown: CostBreakdownProductsFormValues) => {
    if (grCostBreakdown) {
      onSubmit(grCostBreakdown, productsCostBreakdown)
    }
  }

  const handleBack = () => {
    const currentFormData = getValues()
    onBack(currentFormData)
  }

  const handleCancel = () => {
    onCancel(isDirty, isDirty ? getValues() : undefined)
  }

  const renderExpandedProductCostBreakdownForm = (row: Row<PurchaseOrderLineWithReceiptContext>) => (
    <CostBreakdownProductsExpandedContent row={row} goodsReceipt={goodsReceipt!} />
  )

  if (!goodsReceipt || !grCostBreakdown) return null

  return (
    <Dialog open onClose={handleCancel} maxWidth='lg' fullWidth>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogHeader
            title={isEditMode ? "Edit Cost Breakdown: Products" : "Add Cost Breakdown: Products"}
            onClose={handleCancel}
          />
          <DialogContent classes={{ root: "p-10" }}>
            <CostBreakdownPurchaseOrdersTable
              goodsReceipt={goodsReceipt}
              purchaseOrderLineId={purchaseOrderLineId}
              canExpandIncompleteRows
              renderExpandedContent={renderExpandedProductCostBreakdownForm}
            />

            <Box className='mt-6 flex justify-end'>
              <FormNumericField
                control={methods.control}
                name='sharedGrCost'
                label='Shared GR Cost'
                className='w-1/2'
                {...COST_BREAKDOWN_CURRENCY_FIELD_PROPS}
                disabled
                slotProps={{
                  inputLabel: {
                    className: "font-bold",
                  },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions className='flex justify-between px-6 pb-6'>
            <Button
              appearance='outlined'
              onClick={handleCancel}
              disabled={isSubmitting}
              data-testid='cost-breakdown-products-cancel-button'
            >
              Cancel
            </Button>
            <Box className='flex gap-x-3'>
              <Button
                appearance='outlined'
                onClick={handleBack}
                disabled={isSubmitting}
                data-testid='cost-breakdown-products-back-button'
              >
                Go Back
              </Button>
              <Button
                appearance='contained'
                color='primary'
                type='submit'
                disabled={isSubmitting || !isValid}
                data-testid='cost-breakdown-products-finish-button'
              >
                {isSubmitting ? "Saving..." : "Finish"}
              </Button>
            </Box>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
